// ABOUTME: WM 2026 team registry loader and lookup
// ABOUTME: Loads /data/teams.json once at runtime and exposes lookup by FIFA 3-letter code

const TEAMS_URL = './data/teams.json';

let teamsList = null;
let teamsByCode = null;

export function indexTeams(teams) {
    const map = new Map();
    for (const team of teams) {
        map.set(team.code, team);
    }
    return map;
}

export async function loadTeams() {
    if (teamsList) return teamsList;

    const response = await fetch(TEAMS_URL);
    if (!response.ok) {
        throw new Error(`Failed to load teams: ${response.status}`);
    }
    teamsList = await response.json();
    teamsByCode = indexTeams(teamsList);
    return teamsList;
}

export function getAllTeams() {
    if (!teamsList) {
        throw new Error('Teams not loaded. Call loadTeams() first.');
    }
    return teamsList;
}

export function getTeam(code) {
    if (!teamsByCode) {
        throw new Error('Teams not loaded. Call loadTeams() first.');
    }
    return teamsByCode.get(code) ?? null;
}

/**
 * Filter teams by case-insensitive substring match across nameDe, nameEn, nameFr,
 * and code. Empty / whitespace-only queries return the full list unchanged.
 */
export function filterTeams(teams, query) {
    const q = (query ?? '').trim().toLowerCase();
    if (!q) return teams.slice();
    return teams.filter((team) => {
        return (
            team.nameDe.toLowerCase().includes(q) ||
            team.nameEn.toLowerCase().includes(q) ||
            (team.nameFr ?? '').toLowerCase().includes(q) ||
            team.code.toLowerCase().includes(q)
        );
    });
}

// ─── Team picker overlay (browser-side) ─────────────────────────────────────

const DEFAULT_PICKER_CODE = 'SUI';
let pickerEl = null;     // root overlay element
let pickerState = null;  // { resolve, selectedCode }

function ensurePickerDom() {
    if (pickerEl) return pickerEl;
    if (typeof document === 'undefined') {
        throw new Error('showTeamPicker requires a DOM environment');
    }

    pickerEl = document.createElement('div');
    pickerEl.id = 'team-picker-overlay';
    pickerEl.className = 'hidden';
    pickerEl.innerHTML = `
        <div class="picker-backdrop" data-picker-cancel="1"></div>
        <div class="picker-card" role="dialog" aria-labelledby="picker-title" aria-modal="true">
            <header>
                <h2 id="picker-title">Team auswählen</h2>
                <button class="picker-close" aria-label="Abbrechen" data-picker-cancel="1">×</button>
            </header>
            <input type="search" class="picker-search" placeholder="Suchen…" autocomplete="off">
            <div class="picker-grid" role="listbox" aria-label="WM 2026 Teams"></div>
            <footer>
                <button class="picker-confirm" disabled>Bestätigen</button>
            </footer>
        </div>
    `;
    (document.getElementById('app') ?? document.body).appendChild(pickerEl);

    const grid = pickerEl.querySelector('.picker-grid');
    const search = pickerEl.querySelector('.picker-search');
    const confirmBtn = pickerEl.querySelector('.picker-confirm');

    grid.addEventListener('click', onGridClick);
    pickerEl.addEventListener('click', onCancelClick);
    search.addEventListener('input', onSearchInput);
    confirmBtn.addEventListener('click', onConfirmClick);
    document.addEventListener('keydown', onKeydown);

    return pickerEl;
}

function renderTeamTiles(filteredTeams) {
    const grid = pickerEl.querySelector('.picker-grid');
    grid.innerHTML = filteredTeams.map((t) => `
        <button class="team-tile${t.code === pickerState.selectedCode ? ' selected' : ''}"
                data-code="${t.code}" role="option"
                aria-selected="${t.code === pickerState.selectedCode}">
            <img src="${t.emblem}" alt="" loading="lazy" width="40" height="28">
            <span>${t.code}</span>
        </button>
    `).join('');
}

function setSelected(code) {
    pickerState.selectedCode = code;
    pickerEl.querySelectorAll('.team-tile').forEach((tile) => {
        const isSelected = tile.dataset.code === code;
        tile.classList.toggle('selected', isSelected);
        tile.setAttribute('aria-selected', String(isSelected));
    });
    pickerEl.querySelector('.picker-confirm').disabled = !code;
}

function onGridClick(event) {
    const tile = event.target.closest('.team-tile');
    if (!tile) return;
    setSelected(tile.dataset.code);
}

function onCancelClick(event) {
    if (!event.target.dataset || event.target.dataset.pickerCancel !== '1') return;
    finishPicker(null);
}

let searchDebounce = null;
function onSearchInput(event) {
    clearTimeout(searchDebounce);
    const q = event.target.value;
    searchDebounce = setTimeout(() => {
        const filtered = filterTeams(getAllTeams(), q);
        renderTeamTiles(filtered);
    }, 100);
}

function onConfirmClick() {
    if (!pickerState.selectedCode) return;
    finishPicker(pickerState.selectedCode);
}

function onKeydown(event) {
    if (!pickerState) return;
    if (event.key === 'Escape') {
        event.preventDefault();
        finishPicker(null);
    } else if (event.key === 'Enter' && pickerState.selectedCode) {
        event.preventDefault();
        finishPicker(pickerState.selectedCode);
    }
}

function finishPicker(code) {
    if (!pickerState) return;
    const { resolve } = pickerState;
    pickerState = null;
    pickerEl.classList.add('hidden');
    pickerEl.querySelector('.picker-search').value = '';
    resolve(code);
}

/**
 * Show the team picker overlay. Resolves with the selected FIFA code, or
 * `null` if the user cancels (Esc, X, or backdrop click).
 *
 * Calling while another picker is open: the existing one cancels first.
 */
export function showTeamPicker({ defaultCode = DEFAULT_PICKER_CODE } = {}) {
    if (!teamsList) {
        throw new Error('Teams not loaded. Call loadTeams() first.');
    }
    if (pickerState) {
        finishPicker(null);
    }
    ensurePickerDom();

    return new Promise((resolve) => {
        pickerState = { resolve, selectedCode: defaultCode };
        renderTeamTiles(teamsList);
        pickerEl.querySelector('.picker-confirm').disabled = !defaultCode;
        pickerEl.classList.remove('hidden');
        // Focus the search input shortly after open so keyboard users can type immediately
        setTimeout(() => pickerEl.querySelector('.picker-search')?.focus(), 50);
    });
}
